import React, { useState, useEffect, useMemo } from 'react';
import NavigationBar from '../../components/ui/NavigationBar';
import UserStatusIndicator from '../../components/ui/UserStatusIndicator';
import CallStatusWidget from '../../components/ui/CallStatusWidget';
import ContactFilters from './components/ContactFilters';
import ContactList from './components/ContactList';
import ContactDetailPanel from './components/ContactDetailPanel';
import BulkActions from './components/BulkActions';

const ContactDirectory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [sortBy, setSortBy] = useState({ field: 'name', direction: 'asc' });
  const [filters, setFilters] = useState({
    company: '',
    location: '',
    status: '',
    priority: '',
    campaign: ''
  });

  // Mock contact data
  const mockContacts = [
    {
      id: 1,
      name: 'Ahmet Yılmaz',
      phone: '+90 532 123 4567',
      alternativePhone: '+90 212 555 0123',
      email: 'ahmet.yilmaz@teknoloji.com',
      company: 'Teknoloji A.Ş.',
      location: 'İstanbul',
      status: 'Aktif',
      priority: 'Yüksek',
      campaign: 'Yeni Ürün Tanıtımı',
      lastContact: new Date('2025-11-05'),
      notes: `Şirketin IT direktörü. Yeni teknoloji çözümlerine açık.\nBudget onayı için CEO ile görüşmesi gerekiyor.\nTercihen öğleden sonra aranmayı tercih ediyor.`,
      recentInteractions: [
        {
          type: 'call',
          date: '2025-11-05',
          note: 'Ürün demosu planlandı. 15 Kasım tarihinde tekrar görüşülecek.'
        },
        {
          type: 'email',
          date: '2025-11-03',
          note: 'Teknik dokümantasyon gönderildi. Olumlu geri dönüş alındı.'
        }
      ]
    },
    {
      id: 2,
      name: 'Fatma Demir',
      phone: '+90 533 987 6543',
      email: 'fatma.demir@insaat.com',
      company: 'İnşaat Ltd.',
      location: 'Ankara',
      status: 'Beklemede',
      priority: 'Orta',
      campaign: 'Müşteri Memnuniyeti',
      lastContact: new Date('2025-11-04'),
      notes: 'Proje yöneticisi. Maliyet konusunda hassas.',
      recentInteractions: [
        {
          type: 'call',
          date: '2025-11-04',
          note: 'Fiyat teklifi bekleniyor. Pazartesi geri dönüş yapacak.'
        }
      ]
    },
    {
      id: 3,
      name: 'Mehmet Kaya',
      phone: '+90 534 555 1234',
      alternativePhone: '+90 232 444 5678',
      email: 'mehmet.kaya@danismanlik.com',
      company: 'Danışmanlık Hizmetleri',
      location: 'İzmir',
      status: 'Aktif',
      priority: 'Düşük',
      campaign: 'Satış Takibi',
      lastContact: new Date('2025-11-02'),
      notes: 'Uzun vadeli işbirliği potansiyeli var.',
      recentInteractions: [
        {
          type: 'email',
          date: '2025-11-02',
          note: 'Hizmet kataloğu paylaşıldı. İnceleme süreci devam ediyor.'
        }
      ]
    },
    {
      id: 4,
      name: 'Ayşe Özkan',
      phone: '+90 535 777 8899',
      email: 'ayse.ozkan@perakende.com',
      company: 'Perakende Zinciri',
      location: 'Bursa',
      status: 'Pasif',
      priority: 'Yüksek',
      campaign: 'Geri Kazanım',
      lastContact: new Date('2025-10-28'),
      notes: 'Eski müşteri. Yeniden kazanım için özel teklif hazırlanacak.',
      recentInteractions: [
        {
          type: 'call',
          date: '2025-10-28',
          note: 'Memnuniyetsizlik nedenleri dinlendi. Çözüm önerileri sunulacak.'
        }
      ]
    },
    {
      id: 5,
      name: 'Can Arslan',
      phone: '+90 536 333 2211',
      email: 'can.arslan@finans.com',
      company: 'Finans Grubu',
      location: 'İstanbul',
      status: 'Aktif',
      priority: 'Yüksek',
      campaign: 'Yeni Ürün Tanıtımı',
      lastContact: new Date('2025-11-06'),
      notes: 'Finans direktörü. Hızlı karar verme yetkisi var.',
      recentInteractions: [
        {
          type: 'call',
          date: '2025-11-06',
          note: 'Pilot proje için anlaşma imzalandı. İmplementasyon başlayacak.'
        }
      ]
    },
    {
      id: 6,
      name: 'Zeynep Şahin',
      phone: '+90 537 666 4455',
      email: 'zeynep.sahin@teknoloji.com',
      company: 'Teknoloji A.Ş.',
      location: 'İstanbul',
      status: 'Beklemede',
      priority: 'Orta',
      campaign: 'Müşteri Memnuniyeti',
      lastContact: new Date('2025-11-01'),
      notes: 'Teknik ekip lideri. Detaylı analiz yapmayı seviyor.',
      recentInteractions: [
        {
          type: 'email',
          date: '2025-11-01',
          note: 'Teknik sorular yanıtlandı. Karar süreci devam ediyor.'
        }
      ]
    }
  ];

  // Filter and search contacts
  const filteredContacts = useMemo(() => {
    let result = mockContacts;

    // Apply search filter
    if (searchTerm) {
      result = result?.filter(contact =>
        contact?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        contact?.phone?.includes(searchTerm) ||
        contact?.company?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        (contact?.email && contact?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()))
      );
    }

    // Apply filters
    if (filters?.company) {
      result = result?.filter(contact => contact?.company === filters?.company);
    }
    if (filters?.location) {
      result = result?.filter(contact => contact?.location === filters?.location);
    }
    if (filters?.status) {
      result = result?.filter(contact => contact?.status === filters?.status);
    }
    if (filters?.priority) {
      result = result?.filter(contact => contact?.priority === filters?.priority);
    }
    if (filters?.campaign) {
      result = result?.filter(contact => contact?.campaign === filters?.campaign);
    }

    // Apply sorting
    result?.sort((a, b) => {
      let aValue = a?.[sortBy?.field];
      let bValue = b?.[sortBy?.field];

      if (sortBy?.field === 'lastContact') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (typeof aValue === 'string') {
        aValue = aValue?.toLowerCase();
        bValue = bValue?.toLowerCase();
      }

      if (sortBy?.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return result;
  }, [searchTerm, filters, sortBy]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      company: '',
      location: '',
      status: '',
      priority: '',
      campaign: ''
    });
    setSearchTerm('');
  };

  const handleContactSelect = (contact) => {
    setSelectedContact(contact);
  };

  const handleContactCall = (contact) => {
    console.log('Calling contact:', contact?.name, contact?.phone);
    // Simulate call initiation
    alert(`${contact?.name} aranıyor: ${contact?.phone}`);
  };

  const handleContactEdit = (contact) => {
    setSelectedContact(contact);
  };

  const handleContactSave = (updatedContact) => {
    console.log('Saving contact:', updatedContact);
    // In real app, this would update the contact in the database
    setSelectedContact(updatedContact);
  };

  const handleBulkSelect = (contactIds) => {
    setSelectedContacts(contactIds);
  };

  const handleClearSelection = () => {
    setSelectedContacts([]);
  };

  const handleBulkCall = () => {
    const contacts = selectedContacts?.map(id => 
      mockContacts?.find(contact => contact?.id === id)
    )?.filter(Boolean);
    
    console.log('Bulk calling contacts:', contacts);
    alert(`${contacts?.length} kişi toplu arama listesine eklendi`);
  };

  const handleBulkAssignCampaign = (campaignId) => {
    console.log('Assigning campaign:', campaignId, 'to contacts:', selectedContacts);
    alert(`${selectedContacts?.length} kişi kampanyaya atandı`);
    setSelectedContacts([]);
  };

  const handleBulkExport = () => {
    console.log('Exporting contacts:', selectedContacts);
    alert(`${selectedContacts?.length} kişi dışa aktarılıyor`);
  };

  const handleBulkDelete = () => {
    if (window.confirm(`${selectedContacts?.length} kişiyi silmek istediğinizden emin misiniz?`)) {
      console.log('Deleting contacts:', selectedContacts);
      setSelectedContacts([]);
      alert('Seçili kişiler silindi');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />
      <UserStatusIndicator />
      <CallStatusWidget />
      <div className="content-offset">
        <div className="flex h-[calc(100vh-76px)]">
          {/* Main Content */}
          <div className="flex-1 flex flex-col">
            {/* Filters */}
            <ContactFilters
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              totalContacts={mockContacts?.length}
              filteredCount={filteredContacts?.length}
            />

            {/* Bulk Actions */}
            <BulkActions
              selectedContacts={selectedContacts}
              onClearSelection={handleClearSelection}
              onBulkCall={handleBulkCall}
              onBulkAssignCampaign={handleBulkAssignCampaign}
              onBulkExport={handleBulkExport}
              onBulkDelete={handleBulkDelete}
            />

            {/* Contact List */}
            <ContactList
              contacts={filteredContacts}
              selectedContacts={selectedContacts}
              onContactSelect={handleContactSelect}
              onContactCall={handleContactCall}
              onContactEdit={handleContactEdit}
              onBulkSelect={handleBulkSelect}
              sortBy={sortBy}
              onSortChange={setSortBy}
            />
          </div>

          {/* Contact Detail Panel */}
          <ContactDetailPanel
            contact={selectedContact}
            onClose={() => setSelectedContact(null)}
            onSave={handleContactSave}
            onCall={handleContactCall}
          />
        </div>
      </div>
    </div>
  );
};

export default ContactDirectory;